// Deadlines panel (SPEC §3.7 / §4.1.4) — the reusable list + add-form + actions.
//
// Used by the top-level Deadlines page (all deadlines for the active Subject) and
// by the Case Detail "Deadlines" tab (scoped to one Case). When `caseId` is given
// the add-form pre-links that Case, the Case column is hidden, and a "scan
// documents for deadlines" action (re)builds the case's Deadline File.
//
// Derived deadlines (source='derived') land as `proposed` — the panel shows the
// source document + quote and Confirm/Dismiss controls; nothing is authoritative
// until a human confirms (§4.1.4 human-in-the-loop).

import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, deadlines } from "../lib/api";
import { useAsync } from "../hooks/useAsync";
import { pushToast } from "./Toast";
import type { Deadline, DeadlineStatus } from "../lib/types";

const STATUS_STYLES: Record<DeadlineStatus, string> = {
  upcoming: "bg-slate-100 text-slate-600",
  approaching: "bg-amber-100 text-amber-800",
  missed: "bg-red-100 text-red-800",
  met: "bg-green-100 text-green-800",
};

function StatusBadge({ status }: { status: DeadlineStatus }): JSX.Element {
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[status]}`}
    >
      {status}
    </span>
  );
}

export function DeadlinesPanel({ caseId }: { caseId?: number }): JSX.Element {
  const { data, refresh } = useAsync(() => deadlines.list(caseId), [caseId]);
  const rows = data?.deadlines ?? [];

  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const onAdd = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!title.trim() || !dueDate) return;
    setSaving(true);
    try {
      await deadlines.create({
        title: title.trim(),
        due_date: dueDate,
        case_id: caseId ?? null,
        notes: notes.trim() || null,
      });
      setTitle("");
      setDueDate("");
      setNotes("");
      await refresh();
      pushToast("success", "Deadline added");
    } catch (err) {
      pushToast("error", err instanceof ApiError ? err.message : "Could not add deadline");
    } finally {
      setSaving(false);
    }
  };

  // Case tab only: (re)scan the case's documents for stated deadlines.
  const onScan = async (): Promise<void> => {
    if (caseId === undefined) return;
    setScanning(true);
    try {
      const res = await deadlines.rebuildFile(caseId);
      await refresh();
      pushToast(
        "success",
        res.posted > 0
          ? `Found ${res.posted} new deadline${res.posted === 1 ? "" : "s"} to review`
          : "No new deadlines found in this case's documents",
      );
    } catch (err) {
      pushToast("error", err instanceof ApiError ? err.message : "Could not scan for deadlines");
    } finally {
      setScanning(false);
    }
  };

  const act = async (id: number, fn: () => Promise<unknown>, failMsg: string): Promise<void> => {
    setBusyId(id);
    try {
      await fn();
      await refresh();
    } catch (err) {
      pushToast("error", err instanceof ApiError ? err.message : failMsg);
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (d: Deadline): Promise<void> => {
    if (!window.confirm(`Delete deadline “${d.title}”?`)) return;
    await act(d.id, () => deadlines.remove(d.id), "Could not delete deadline");
  };

  const onExport = async (): Promise<void> => {
    setExporting(true);
    try {
      const blob = await deadlines.exportIcs(caseId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "caselens-deadlines.ics";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      pushToast("error", err instanceof ApiError ? err.message : "Could not export calendar");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={onAdd} className="card grid grid-cols-1 gap-3 p-4 sm:grid-cols-[1fr,auto,auto]">
        <div>
          <label htmlFor="dl-title" className="label">
            Deadline
          </label>
          <input
            id="dl-title"
            className="input"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. File appeal of adverse determination"
          />
        </div>
        <div>
          <label htmlFor="dl-date" className="label">
            Due date
          </label>
          <input
            id="dl-date"
            type="date"
            className="input"
            required
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </div>
        <div className="flex items-end">
          <button
            type="submit"
            className="btn-primary w-full sm:w-auto"
            disabled={saving || !title.trim() || !dueDate}
          >
            {saving ? "Adding…" : "Add"}
          </button>
        </div>
        <div className="sm:col-span-3">
          <input
            className="input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)"
          />
        </div>
      </form>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">
          {rows.length === 0
            ? "No deadlines yet."
            : `${rows.length} deadline${rows.length === 1 ? "" : "s"}`}
        </p>
        <div className="flex gap-2">
          {caseId !== undefined && (
            <button type="button" className="btn-secondary" onClick={onScan} disabled={scanning}>
              {scanning ? "Scanning…" : "Scan documents for deadlines"}
            </button>
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={onExport}
            disabled={exporting || rows.length === 0}
          >
            {exporting ? "Exporting…" : "Export .ics"}
          </button>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Deadline</th>
                <th className="px-4 py-2">Due</th>
                <th className="px-4 py-2">Status</th>
                {caseId === undefined && <th className="px-4 py-2">Case</th>}
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((d) => {
                const proposed = d.source === "derived" && d.review_state === "proposed";
                return (
                  <tr key={d.id} className={d.is_met ? "text-slate-400" : ""}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-slate-800">{d.title}</div>
                      {d.notes && <div className="text-xs text-slate-500">{d.notes}</div>}
                      {d.source === "derived" && d.source_quote && (
                        <div className="mt-1 border-l-2 border-slate-200 pl-2 text-xs italic text-slate-500">
                          “{d.source_quote}”
                          {d.triggering_document_id && (
                            <>
                              {" "}
                              <Link
                                to={`/documents/${d.triggering_document_id}`}
                                className="not-italic text-brand-700 hover:underline"
                              >
                                (source doc #{d.triggering_document_id})
                              </Link>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2">{d.due_date}</td>
                    <td className="space-x-1 px-4 py-2">
                      <StatusBadge status={d.status} />
                      {proposed && (
                        <span className="inline-block rounded bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800">
                          Proposed
                        </span>
                      )}
                      {d.source === "derived" && d.verification_status === "unverified" && (
                        <span
                          className="inline-block rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800"
                          title="The cited statute could not be verified against the corpus"
                        >
                          Unverified basis
                        </span>
                      )}
                      {/* Whose duty this is (slice 2c.2). Shown on derived rows
                          because confirming one is what makes it authoritative:
                          a confirmed, verified, missed CARRIER deadline becomes a
                          standalone DWC-154 violation (SPEC §3.7), so the user
                          must see the attribution before vouching for it. */}
                      {d.source === "derived" && d.obligated_party === "carrier" && (
                        <span
                          className="inline-block rounded bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-800"
                          title="The carrier owes this response. If it lapses, the DWC-154 Writer can raise it as a violation — confirm only if that's right."
                        >
                          Carrier owes
                        </span>
                      )}
                      {d.source === "derived" && d.obligated_party === "claimant" && (
                        <span
                          className="inline-block rounded bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800"
                          title="You owe this response"
                        >
                          You owe
                        </span>
                      )}
                      {d.source === "derived" && d.obligated_party === "unknown" && (
                        <span
                          className="inline-block rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
                          title="Could not tell whose duty this is from the document. It will not be raised as a carrier violation."
                        >
                          Party unclear
                        </span>
                      )}
                    </td>
                    {caseId === undefined && (
                      <td className="px-4 py-2">
                        {d.case_id ? (
                          <Link to={`/cases/${d.case_id}`} className="text-brand-700 hover:underline">
                            #{d.case_id}
                          </Link>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    )}
                    <td className="whitespace-nowrap px-4 py-2 text-right">
                      {proposed ? (
                        <>
                          <button
                            type="button"
                            className="mr-3 text-brand-700 hover:underline disabled:opacity-50"
                            onClick={() =>
                              act(d.id, () => deadlines.confirm(d.id), "Could not confirm deadline")
                            }
                            disabled={busyId === d.id}
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            className="text-slate-500 hover:underline disabled:opacity-50"
                            onClick={() =>
                              act(d.id, () => deadlines.dismiss(d.id), "Could not dismiss deadline")
                            }
                            disabled={busyId === d.id}
                          >
                            Dismiss
                          </button>
                        </>
                      ) : (
                        <>
                          {!d.is_met && (
                            <button
                              type="button"
                              className="mr-3 text-brand-700 hover:underline disabled:opacity-50"
                              onClick={() =>
                                act(d.id, () => deadlines.markMet(d.id), "Could not update deadline")
                              }
                              disabled={busyId === d.id}
                            >
                              Mark met
                            </button>
                          )}
                          <button
                            type="button"
                            className="text-red-600 hover:underline disabled:opacity-50"
                            onClick={() => onDelete(d)}
                            disabled={busyId === d.id}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
